import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, ExternalLink, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

/**
 * Super Admin — Full Site Content CMS (v2).
 *
 * Every visible section on the landing page is editable, with per-section
 * show/hide + ordering, and per-field reset. Marketing SEO meta editor
 * covers home + pricing + about + contact.
 */

const DEFAULTS = {
    landing_hero: {
        eyebrow: "AI-refined · Doctor-approved · Publish-ready",
        headline_l1: "Build Your Professional Identity.",
        headline_l2: "Grow Your Practice.",
        headline_l3: "Leave a Legacy.",
        subheading: "A verified public presence at /dr/<your-name> — refined by AI, approved by admins and by you.",
        cta_primary: "Create Your Doctor Digital Identity",
        cta_secondary: "See packages",
        cta_demo: "View Live Demo",
        cta_score: "Check FREE Identity Score",
        stat_features_label: "Curated features",
        stat_templates_label: "Design templates",
        stat_packages_label: "Package tiers",
    },
    landing_trust: [
        { label: "Grounded AI", value: "Zero fabrication" },
        { label: "Approvals", value: "Admin + Doctor" },
        { label: "Public URL", value: "/dr/<your-name>" },
        { label: "Payments", value: "50% + 50% GST" },
    ],
    landing_what: {
        eyebrow: "What it is",
        heading: "A single verified home for your professional identity.",
        body_p1: "Doctor Digital Identity is a curated, verified profile — refined by AI, reviewed by admins, and approved by you — hosted at a public URL you own. It is not a diagnostic platform, hospital directory, or emergency service.",
        body_p2: "Every piece of content is grounded in facts you provide. If a section can't be sourced from your input, the platform explicitly flags it as \"Information Required\" — never fabricated.",
    },
    landing_modules_intro: {
        eyebrow_prefix: "15 modules ·",
        heading: "Everything a doctor's digital presence needs.",
        body: "Feature modules are unlocked by package tier. Every module is DB-driven — the entitlement engine, not the code, decides what appears in your workspace.",
    },
    landing_modules: [
        { code: "A", name: "Digital Identity", desc: "Verified profile, slug URL, credentials." },
        { code: "B", name: "Brand Building", desc: "Personal bio, brand colors, storytelling." },
        { code: "C", name: "Patient Info", desc: "Consultation hours, fees, insurance." },
        { code: "D", name: "Educational Content", desc: "Articles, condition guides, videos." },
        { code: "E", name: "Social Media", desc: "Cross-platform composer & GMB checklist." },
        { code: "F", name: "Achievements", desc: "Awards, certifications, milestones." },
        { code: "G", name: "Reputation", desc: "Reviews, testimonials, sentiment." },
        { code: "H", name: "AI Content Engine", desc: "Refined bios, SEO, FAQ, drafts." },
        { code: "I", name: "Media & Gallery", desc: "Photos, videos, 3D tour, reels." },
        { code: "J", name: "Patient Services", desc: "Enquiry, telemedicine, portal." },
        { code: "K", name: "Social Impact", desc: "Camps, CSR, impact metrics." },
        { code: "L", name: "Documents", desc: "CV, brochures, research repository." },
        { code: "M", name: "Security & Data", desc: "Encryption, RBAC, audit logs." },
        { code: "N", name: "Legacy", desc: "Timeline, mentorship, memorial page." },
        { code: "O", name: "Continuous Service", desc: "Success, refresh, strategy." },
    ],
    landing_how_intro: {
        eyebrow: "How it works",
        heading: "From sign-up to a published identity in 5 clear steps.",
    },
    landing_how_steps: [
        { n: "01", title: "Sign up & verify", body: "Register with your medical registration number, verify OTP, and choose a package." },
        { n: "02", title: "Guided onboarding", body: "Complete 8 structured steps: identity, education, experience, clinics, credentials." },
        { n: "03", title: "AI content refinement", body: "Our grounded AI writes bios, SEO meta, and FAQs — never fabricates, always cites your data." },
        { n: "04", title: "Admin + doctor approval", body: "Every published word passes admin review and requires your explicit doctor approval." },
        { n: "05", title: "Publish & grow", body: "Your public identity goes live at /dr/<your-name> with QR card, SEO, and social kit." },
    ],
    landing_ai: {
        eyebrow: "AI content engine",
        heading: "Grounded AI. Zero fabrication. Always yours to approve.",
        body: "Our async pipeline drafts your biography, profile summary, SEO meta, and FAQs from the facts you supply. Missing data is never invented — it's flagged \"Information Required\" and returned for your input.",
        bullets: [
            "No fabricated qualifications, awards, or affiliations.",
            "Prompt version + model + input reference stored per job.",
            "You review and approve every AI-generated section.",
            "Regenerate any section with one click.",
        ],
    },
    landing_templates_intro: {
        eyebrow_prefix: "premium templates · 5 layout engines",
        heading: "One profile. Five distinct layout engines. Your identity, your way.",
        body: "172 premium templates across 5 layout engines — Editorial Spread, Journey First, Bento Cards, Typographic Minimal and the flagship Verified Pro. Every layout ships with the same trust primitives (QR card, verified badge, real stats, articles, gallery, testimonials) — what changes is the composition, typography and rhythm.",
        cta: "Preview all 172 templates after sign-up",
    },
    landing_packages_intro: {
        eyebrow: "Packages",
        heading: "Pick the identity tier that matches your ambition.",
        body: "All prices in ₹ (INR), exclusive of GST. Pay 50% advance to begin onboarding, 50% before publication. AMC billed annually from year 2.",
        compare_cta: "Full feature comparison",
    },
    landing_qr: {
        eyebrow: "Digital visiting card",
        heading: "One QR code. Every patient who scans it lands on your verified page.",
        body: "Print it on prescription slips, WhatsApp-DM it to referrals, add it to your clinic's reception. The QR routes to your public digital identity — refreshed automatically whenever you publish an update.",
        bullets: [
            "Ready-to-print vCard, PDF and PNG downloads.",
            "Share button on public page — WhatsApp, LinkedIn, X, e-mail.",
            "Every scan is anonymised & captured in your analytics.",
        ],
    },
    landing_security_intro: {
        eyebrow: "Security & data",
        heading: "Built with tenant isolation, audit logs, and zero shortcuts.",
    },
    landing_final_cta: {
        heading: "Ready to publish a professional identity your patients will trust?",
        body: "Sign up in under two minutes. Complete onboarding at your pace. Publish with dual approval — refined by AI, approved by you.",
        cta_primary: "Create Your Doctor Digital Identity",
        cta_secondary: "See packages",
        disclaimer: "No credit card required to sign up. Advance payment starts onboarding.",
    },
    testimonials: [
        { quote: "The verified profile changed how patients discover me.", author: "Dr. Demo Sharma", role: "Cardiologist" },
    ],
    faqs: [
        { question: "Is my data private?", answer: "Yes — you own everything and approve every published word." },
    ],
    footer: {
        tagline: "Verified digital identity for practicing physicians.",
        legal: "© 2026 Doctor Digital Identity Platform. All rights reserved.",
    },
    contact: {
        support_email: "support@doctorsprofile.in",
        support_phone: "+91 77220 90290",
        office_address: "",
    },
    marketing_seo: {
        home: { title: "Doctor Digital Identity — AI-Refined Website & QR Card for Indian Doctors",
                description: "Verified public profile at /dr/<your-name>. AI-refined bio + SEO. Admin-reviewed. Doctor-approved." },
        pricing: { title: "Pricing — Doctor Digital Identity", description: "5 tiers from ₹5k to ₹20k. Pay 50% + 50%. GST inclusive." },
        about: { title: "About — Doctor Digital Identity", description: "The team, mission, and rules behind India's grounded-AI doctor identity platform." },
        contact: { title: "Contact — Doctor Digital Identity", description: "Reach the Doctor Digital Identity support team." },
    },
    landing_sections: [
        { id: "hero", label: "Hero", visible: true },
        { id: "trust", label: "Trust strip", visible: true },
        { id: "what", label: "What is it", visible: true },
        { id: "modules", label: "Modules grid", visible: true },
        { id: "how", label: "How it works", visible: true },
        { id: "ai", label: "AI section", visible: true },
        { id: "templates", label: "Templates gallery", visible: true },
        { id: "packages", label: "Packages/pricing", visible: true },
        { id: "qr", label: "QR / visiting card", visible: true },
        { id: "security", label: "Security & data", visible: true },
        { id: "faq", label: "FAQ", visible: true },
        { id: "final_cta", label: "Final CTA", visible: true },
    ],
};

const SECTION_TABS = [
    { key: "hero", label: "Hero" },
    { key: "trust", label: "Trust strip" },
    { key: "what", label: "What is it" },
    { key: "modules", label: "Modules" },
    { key: "how", label: "How it works" },
    { key: "ai", label: "AI section" },
    { key: "templates", label: "Templates" },
    { key: "packages", label: "Packages" },
    { key: "qr", label: "QR / card" },
    { key: "security", label: "Security" },
    { key: "testimonials", label: "Testimonials" },
    { key: "faqs", label: "FAQ" },
    { key: "final_cta", label: "Final CTA" },
    { key: "footer", label: "Footer" },
    { key: "contact", label: "Contact" },
    { key: "seo", label: "SEO meta" },
    { key: "legal", label: "Legal" },
    { key: "layout", label: "Layout · Order" },
];

export default function SuperAdminSiteContent() {
    const [content, setContent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("hero");
    const [restoreOpen, setRestoreOpen] = useState(false);
    const [restoring, setRestoring] = useState(false);

    useEffect(() => {
        api.get("/super-admin/site-content")
            .then((r) => setContent(mergeDefaults(r.data || {})))
            .catch(() => setContent(mergeDefaults({})));
    }, []);

    function mergeDefaults(incoming) {
        const out = JSON.parse(JSON.stringify(DEFAULTS));
        for (const [k, v] of Object.entries(incoming || {})) {
            out[k] = v;
        }
        return out;
    }

    async function save(sectionKey, patchObj) {
        setSaving(true);
        try {
            const patch = patchObj || { [sectionKey]: content[sectionKey] };
            await api.put("/super-admin/site-content", patch);
            toast.success(`Saved`);
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setSaving(false); }
    }

    async function restoreDefaults() {
        setRestoring(true);
        try {
            const r = await api.post("/super-admin/site-content/restore-defaults");
            setContent(mergeDefaults(r.data?.content || {}));
            toast.success(`Restored ${(r.data?.restored_keys || []).length} sections to defaults`);
            setRestoreOpen(false);
        } catch (e) { toast.error(errMsg(e, "Restore failed")); }
        finally { setRestoring(false); }
    }

    function resetSection(key) {
        setContent({ ...content, [key]: JSON.parse(JSON.stringify(DEFAULTS[key])) });
        toast.info(`${key} reset (unsaved — click Save to persist)`);
    }
    function resetField(sectionKey, fieldKey) {
        const def = DEFAULTS[sectionKey];
        if (!def) return;
        setContent({ ...content, [sectionKey]: { ...content[sectionKey], [fieldKey]: def[fieldKey] } });
    }

    function updateKey(section, key, value) {
        setContent({ ...content, [section]: { ...content[section], [key]: value } });
    }
    function updateArrayItem(section, idx, key, value) {
        const arr = [...(content[section] || [])];
        arr[idx] = { ...arr[idx], [key]: value };
        setContent({ ...content, [section]: arr });
    }
    function addArrayItem(section, template) {
        setContent({ ...content, [section]: [...(content[section] || []), template] });
    }
    function removeArrayItem(section, idx) {
        const arr = [...(content[section] || [])];
        arr.splice(idx, 1);
        setContent({ ...content, [section]: arr });
    }

    // Layout order + visibility
    function toggleSectionVisible(id) {
        const secs = (content.landing_sections || DEFAULTS.landing_sections).map((s) =>
            s.id === id ? { ...s, visible: !s.visible } : s);
        setContent({ ...content, landing_sections: secs });
    }
    function moveSection(id, dir) {
        const secs = [...(content.landing_sections || DEFAULTS.landing_sections)];
        const i = secs.findIndex((s) => s.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= secs.length) return;
        [secs[i], secs[j]] = [secs[j], secs[i]];
        setContent({ ...content, landing_sections: secs });
    }

    if (!content) return <AdminShell><div className="text-[#17212B]/60">Loading…</div></AdminShell>;

    return (
        <AdminShell>
            <div className="mb-4 flex items-start justify-between" data-testid="site-content-header">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin · Site Content CMS</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">Landing page & marketing copy</h1>
                    <p className="text-[#17212B]/60 mt-1 text-sm">
                        Every landing section, testimonials, FAQ, footer, contact, legal, and marketing SEO meta — editable here. Save per section; hide/reorder layout at bottom.
                    </p>
                </div>
                <a href="/" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#2563EB] hover:underline"
                    data-testid="cms-open-landing">
                    Open landing <ExternalLink size={14} />
                </a>
            </div>

            <div className="mb-4 flex items-center justify-end">
                <Button variant="outline" size="sm" onClick={() => setRestoreOpen(true)}
                    className="border-[#D97706] text-[#B45309] hover:bg-[#FFFBEB]"
                    data-testid="cms-restore-defaults-open">
                    <RotateCcw size={13} className="mr-1.5" /> Restore all to defaults
                </Button>
            </div>

            {restoreOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    onClick={() => !restoring && setRestoreOpen(false)}
                    data-testid="cms-restore-confirm-modal">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle className="text-[#D97706] shrink-0 mt-0.5" size={20} />
                            <div>
                                <h3 className="text-lg font-bold text-[#123B5D]">Restore all landing content to defaults?</h3>
                                <p className="text-sm text-[#17212B]/70 mt-2">
                                    All landing sections, testimonials, FAQs, footer, contact info, and layout ordering will return to shipped defaults.
                                </p>
                                <p className="text-xs text-[#B45309] mt-2 font-semibold">
                                    Custom testimonials, FAQs, and section edits made via CMS will be overwritten.
                                </p>
                                <p className="text-xs text-[#17212B]/60 mt-2">
                                    Marketing SEO meta and legal HTML (terms, privacy, refund) are preserved.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end mt-4">
                            <Button variant="outline" size="sm" onClick={() => setRestoreOpen(false)}
                                disabled={restoring} data-testid="cms-restore-cancel">
                                Cancel
                            </Button>
                            <Button size="sm" onClick={restoreDefaults} disabled={restoring}
                                className="bg-[#D97706] hover:bg-[#B45309]"
                                data-testid="cms-restore-confirm">
                                <RotateCcw size={13} className="mr-1.5" />
                                {restoring ? "Restoring…" : "Restore defaults"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="flex-wrap h-auto" data-testid="cms-tabs">
                    {SECTION_TABS.map((t) => (
                        <TabsTrigger key={t.key} value={t.key} data-testid={`cms-tab-${t.key}`} className="text-xs">
                            {t.label}
                        </TabsTrigger>
                    ))}
                </TabsList>

                {/* HERO */}
                <TabsContent value="hero" className="mt-4">
                    <SectionCard title="Hero" onSave={() => save("landing_hero")} onReset={() => resetSection("landing_hero")} saving={saving} testId="cms-hero">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_hero?.eyebrow} onReset={() => resetField("landing_hero", "eyebrow")} defaultVal={DEFAULTS.landing_hero.eyebrow} onChange={(v) => updateKey("landing_hero", "eyebrow", v)} testId="cms-hero-eyebrow" />
                            <FieldCol label="Primary CTA" value={content.landing_hero?.cta_primary} onReset={() => resetField("landing_hero", "cta_primary")} defaultVal={DEFAULTS.landing_hero.cta_primary} onChange={(v) => updateKey("landing_hero", "cta_primary", v)} testId="cms-hero-cta1" />
                            <FieldCol label="Secondary CTA" value={content.landing_hero?.cta_secondary} onReset={() => resetField("landing_hero", "cta_secondary")} defaultVal={DEFAULTS.landing_hero.cta_secondary} onChange={(v) => updateKey("landing_hero", "cta_secondary", v)} testId="cms-hero-cta2" />
                        </FieldRow>
                        <FieldRow>
                            <FieldCol label="Headline line 1" value={content.landing_hero?.headline_l1} onReset={() => resetField("landing_hero", "headline_l1")} defaultVal={DEFAULTS.landing_hero.headline_l1} onChange={(v) => updateKey("landing_hero", "headline_l1", v)} testId="cms-hero-l1" />
                            <FieldCol label="Headline line 2 (accent)" value={content.landing_hero?.headline_l2} onReset={() => resetField("landing_hero", "headline_l2")} defaultVal={DEFAULTS.landing_hero.headline_l2} onChange={(v) => updateKey("landing_hero", "headline_l2", v)} testId="cms-hero-l2" />
                            <FieldCol label="Headline line 3" value={content.landing_hero?.headline_l3} onReset={() => resetField("landing_hero", "headline_l3")} defaultVal={DEFAULTS.landing_hero.headline_l3} onChange={(v) => updateKey("landing_hero", "headline_l3", v)} testId="cms-hero-l3" />
                        </FieldRow>
                        <TextareaField label="Sub-heading" rows={2} value={content.landing_hero?.subheading} onReset={() => resetField("landing_hero", "subheading")} defaultVal={DEFAULTS.landing_hero.subheading} onChange={(v) => updateKey("landing_hero", "subheading", v)} testId="cms-hero-sub" />
                        <FieldRow>
                            <FieldCol label="Demo CTA label" value={content.landing_hero?.cta_demo} onReset={() => resetField("landing_hero", "cta_demo")} defaultVal={DEFAULTS.landing_hero.cta_demo} onChange={(v) => updateKey("landing_hero", "cta_demo", v)} testId="cms-hero-cta-demo" />
                            <FieldCol label="Score CTA label" value={content.landing_hero?.cta_score} onReset={() => resetField("landing_hero", "cta_score")} defaultVal={DEFAULTS.landing_hero.cta_score} onChange={(v) => updateKey("landing_hero", "cta_score", v)} testId="cms-hero-cta-score" />
                        </FieldRow>
                        <FieldRow>
                            <FieldCol label="Stat 1 label" value={content.landing_hero?.stat_features_label} onReset={() => resetField("landing_hero", "stat_features_label")} defaultVal={DEFAULTS.landing_hero.stat_features_label} onChange={(v) => updateKey("landing_hero", "stat_features_label", v)} testId="cms-hero-stat1" />
                            <FieldCol label="Stat 2 label" value={content.landing_hero?.stat_templates_label} onReset={() => resetField("landing_hero", "stat_templates_label")} defaultVal={DEFAULTS.landing_hero.stat_templates_label} onChange={(v) => updateKey("landing_hero", "stat_templates_label", v)} testId="cms-hero-stat2" />
                            <FieldCol label="Stat 3 label" value={content.landing_hero?.stat_packages_label} onReset={() => resetField("landing_hero", "stat_packages_label")} defaultVal={DEFAULTS.landing_hero.stat_packages_label} onChange={(v) => updateKey("landing_hero", "stat_packages_label", v)} testId="cms-hero-stat3" />
                        </FieldRow>
                    </SectionCard>
                </TabsContent>

                {/* TRUST */}
                <TabsContent value="trust" className="mt-4">
                    <ArrayCard
                        title="Trust strip (4 items)"
                        section="landing_trust"
                        items={content.landing_trust || []}
                        fields={[{ key: "label", label: "Label" }, { key: "value", label: "Value" }]}
                        onUpdate={updateArrayItem}
                        onAdd={() => addArrayItem("landing_trust", { label: "", value: "" })}
                        onRemove={removeArrayItem}
                        onSave={() => save("landing_trust")}
                        onReset={() => resetSection("landing_trust")}
                        saving={saving}
                        testId="cms-trust"
                    />
                </TabsContent>

                {/* WHAT */}
                <TabsContent value="what" className="mt-4">
                    <SectionCard title="What is it" onSave={() => save("landing_what")} onReset={() => resetSection("landing_what")} saving={saving} testId="cms-what">
                        <FieldCol label="Eyebrow" value={content.landing_what?.eyebrow} onReset={() => resetField("landing_what", "eyebrow")} defaultVal={DEFAULTS.landing_what.eyebrow} onChange={(v) => updateKey("landing_what", "eyebrow", v)} testId="cms-what-eyebrow" />
                        <TextareaField label="Heading" rows={2} value={content.landing_what?.heading} onReset={() => resetField("landing_what", "heading")} defaultVal={DEFAULTS.landing_what.heading} onChange={(v) => updateKey("landing_what", "heading", v)} testId="cms-what-heading" />
                        <TextareaField label="Paragraph 1" rows={3} value={content.landing_what?.body_p1} onReset={() => resetField("landing_what", "body_p1")} defaultVal={DEFAULTS.landing_what.body_p1} onChange={(v) => updateKey("landing_what", "body_p1", v)} testId="cms-what-p1" />
                        <TextareaField label="Paragraph 2" rows={3} value={content.landing_what?.body_p2} onReset={() => resetField("landing_what", "body_p2")} defaultVal={DEFAULTS.landing_what.body_p2} onChange={(v) => updateKey("landing_what", "body_p2", v)} testId="cms-what-p2" />
                    </SectionCard>
                </TabsContent>

                {/* MODULES */}
                <TabsContent value="modules" className="mt-4 space-y-4">
                    <SectionCard title="Modules section intro" onSave={() => save("landing_modules_intro")} onReset={() => resetSection("landing_modules_intro")} saving={saving} testId="cms-modules-intro">
                        <FieldRow>
                            <FieldCol label="Eyebrow prefix" value={content.landing_modules_intro?.eyebrow_prefix} onReset={() => resetField("landing_modules_intro", "eyebrow_prefix")} defaultVal={DEFAULTS.landing_modules_intro.eyebrow_prefix} onChange={(v) => updateKey("landing_modules_intro", "eyebrow_prefix", v)} testId="cms-mods-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_modules_intro?.heading} onReset={() => resetField("landing_modules_intro", "heading")} defaultVal={DEFAULTS.landing_modules_intro.heading} onChange={(v) => updateKey("landing_modules_intro", "heading", v)} testId="cms-mods-heading" />
                        </FieldRow>
                        <TextareaField label="Body" rows={3} value={content.landing_modules_intro?.body} onReset={() => resetField("landing_modules_intro", "body")} defaultVal={DEFAULTS.landing_modules_intro.body} onChange={(v) => updateKey("landing_modules_intro", "body", v)} testId="cms-mods-body" />
                    </SectionCard>
                    <ArrayCard title="Module cards (15)"
                        section="landing_modules" items={content.landing_modules || []}
                        fields={[{ key: "code", label: "Code (A–O)" }, { key: "name", label: "Name" }, { key: "desc", label: "Description", isTextarea: true }]}
                        onUpdate={updateArrayItem}
                        onAdd={() => addArrayItem("landing_modules", { code: "", name: "", desc: "" })}
                        onRemove={removeArrayItem}
                        onSave={() => save("landing_modules")}
                        onReset={() => resetSection("landing_modules")}
                        saving={saving} testId="cms-modules" />
                </TabsContent>

                {/* HOW */}
                <TabsContent value="how" className="mt-4 space-y-4">
                    <SectionCard title="How-it-works intro" onSave={() => save("landing_how_intro")} onReset={() => resetSection("landing_how_intro")} saving={saving} testId="cms-how-intro">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_how_intro?.eyebrow} onReset={() => resetField("landing_how_intro", "eyebrow")} defaultVal={DEFAULTS.landing_how_intro.eyebrow} onChange={(v) => updateKey("landing_how_intro", "eyebrow", v)} testId="cms-how-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_how_intro?.heading} onReset={() => resetField("landing_how_intro", "heading")} defaultVal={DEFAULTS.landing_how_intro.heading} onChange={(v) => updateKey("landing_how_intro", "heading", v)} testId="cms-how-heading" />
                        </FieldRow>
                    </SectionCard>
                    <ArrayCard title="Steps" section="landing_how_steps" items={content.landing_how_steps || []}
                        fields={[{ key: "n", label: "Number" }, { key: "title", label: "Title" }, { key: "body", label: "Body", isTextarea: true }]}
                        onUpdate={updateArrayItem}
                        onAdd={() => addArrayItem("landing_how_steps", { n: "", title: "", body: "" })}
                        onRemove={removeArrayItem}
                        onSave={() => save("landing_how_steps")}
                        onReset={() => resetSection("landing_how_steps")}
                        saving={saving} testId="cms-how" />
                </TabsContent>

                {/* AI */}
                <TabsContent value="ai" className="mt-4 space-y-4">
                    <SectionCard title="AI section" onSave={() => save("landing_ai")} onReset={() => resetSection("landing_ai")} saving={saving} testId="cms-ai">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_ai?.eyebrow} onReset={() => resetField("landing_ai", "eyebrow")} defaultVal={DEFAULTS.landing_ai.eyebrow} onChange={(v) => updateKey("landing_ai", "eyebrow", v)} testId="cms-ai-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_ai?.heading} onReset={() => resetField("landing_ai", "heading")} defaultVal={DEFAULTS.landing_ai.heading} onChange={(v) => updateKey("landing_ai", "heading", v)} testId="cms-ai-heading" />
                        </FieldRow>
                        <TextareaField label="Body" rows={3} value={content.landing_ai?.body} onReset={() => resetField("landing_ai", "body")} defaultVal={DEFAULTS.landing_ai.body} onChange={(v) => updateKey("landing_ai", "body", v)} testId="cms-ai-body" />
                        <div>
                            <Label className="text-xs">Bullets (one per line)</Label>
                            <Textarea rows={5} value={(content.landing_ai?.bullets || []).join("\n")}
                                onChange={(e) => updateKey("landing_ai", "bullets", e.target.value.split("\n").filter(Boolean))}
                                data-testid="cms-ai-bullets" />
                        </div>
                    </SectionCard>
                </TabsContent>

                {/* TEMPLATES */}
                <TabsContent value="templates" className="mt-4">
                    <SectionCard title="Templates gallery intro" onSave={() => save("landing_templates_intro")} onReset={() => resetSection("landing_templates_intro")} saving={saving} testId="cms-templates">
                        <FieldRow>
                            <FieldCol label="Eyebrow suffix (before it renders '<n> templates · 7 families')" value={content.landing_templates_intro?.eyebrow_prefix} onReset={() => resetField("landing_templates_intro", "eyebrow_prefix")} defaultVal={DEFAULTS.landing_templates_intro.eyebrow_prefix} onChange={(v) => updateKey("landing_templates_intro", "eyebrow_prefix", v)} testId="cms-tpl-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_templates_intro?.heading} onReset={() => resetField("landing_templates_intro", "heading")} defaultVal={DEFAULTS.landing_templates_intro.heading} onChange={(v) => updateKey("landing_templates_intro", "heading", v)} testId="cms-tpl-heading" />
                        </FieldRow>
                        <TextareaField label="Body" rows={3} value={content.landing_templates_intro?.body} onReset={() => resetField("landing_templates_intro", "body")} defaultVal={DEFAULTS.landing_templates_intro.body} onChange={(v) => updateKey("landing_templates_intro", "body", v)} testId="cms-tpl-body" />
                        <FieldCol label="CTA label" value={content.landing_templates_intro?.cta} onReset={() => resetField("landing_templates_intro", "cta")} defaultVal={DEFAULTS.landing_templates_intro.cta} onChange={(v) => updateKey("landing_templates_intro", "cta", v)} testId="cms-tpl-cta" />
                    </SectionCard>
                </TabsContent>

                {/* PACKAGES */}
                <TabsContent value="packages" className="mt-4">
                    <SectionCard title="Packages intro" onSave={() => save("landing_packages_intro")} onReset={() => resetSection("landing_packages_intro")} saving={saving} testId="cms-packages">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_packages_intro?.eyebrow} onReset={() => resetField("landing_packages_intro", "eyebrow")} defaultVal={DEFAULTS.landing_packages_intro.eyebrow} onChange={(v) => updateKey("landing_packages_intro", "eyebrow", v)} testId="cms-pkg-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_packages_intro?.heading} onReset={() => resetField("landing_packages_intro", "heading")} defaultVal={DEFAULTS.landing_packages_intro.heading} onChange={(v) => updateKey("landing_packages_intro", "heading", v)} testId="cms-pkg-heading" />
                        </FieldRow>
                        <TextareaField label="Body" rows={3} value={content.landing_packages_intro?.body} onReset={() => resetField("landing_packages_intro", "body")} defaultVal={DEFAULTS.landing_packages_intro.body} onChange={(v) => updateKey("landing_packages_intro", "body", v)} testId="cms-pkg-body" />
                        <FieldCol label="Compare CTA label" value={content.landing_packages_intro?.compare_cta} onReset={() => resetField("landing_packages_intro", "compare_cta")} defaultVal={DEFAULTS.landing_packages_intro.compare_cta} onChange={(v) => updateKey("landing_packages_intro", "compare_cta", v)} testId="cms-pkg-cta" />
                    </SectionCard>
                </TabsContent>

                {/* QR */}
                <TabsContent value="qr" className="mt-4">
                    <SectionCard title="QR / visiting card" onSave={() => save("landing_qr")} onReset={() => resetSection("landing_qr")} saving={saving} testId="cms-qr">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_qr?.eyebrow} onReset={() => resetField("landing_qr", "eyebrow")} defaultVal={DEFAULTS.landing_qr.eyebrow} onChange={(v) => updateKey("landing_qr", "eyebrow", v)} testId="cms-qr-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_qr?.heading} onReset={() => resetField("landing_qr", "heading")} defaultVal={DEFAULTS.landing_qr.heading} onChange={(v) => updateKey("landing_qr", "heading", v)} testId="cms-qr-heading" />
                        </FieldRow>
                        <TextareaField label="Body" rows={3} value={content.landing_qr?.body} onReset={() => resetField("landing_qr", "body")} defaultVal={DEFAULTS.landing_qr.body} onChange={(v) => updateKey("landing_qr", "body", v)} testId="cms-qr-body" />
                        <div>
                            <Label className="text-xs">Bullets (one per line)</Label>
                            <Textarea rows={4} value={(content.landing_qr?.bullets || []).join("\n")}
                                onChange={(e) => updateKey("landing_qr", "bullets", e.target.value.split("\n").filter(Boolean))}
                                data-testid="cms-qr-bullets" />
                        </div>
                    </SectionCard>
                </TabsContent>

                {/* SECURITY */}
                <TabsContent value="security" className="mt-4">
                    <SectionCard title="Security section intro" onSave={() => save("landing_security_intro")} onReset={() => resetSection("landing_security_intro")} saving={saving} testId="cms-security">
                        <FieldRow>
                            <FieldCol label="Eyebrow" value={content.landing_security_intro?.eyebrow} onReset={() => resetField("landing_security_intro", "eyebrow")} defaultVal={DEFAULTS.landing_security_intro.eyebrow} onChange={(v) => updateKey("landing_security_intro", "eyebrow", v)} testId="cms-sec-eyebrow" />
                            <FieldCol label="Heading" value={content.landing_security_intro?.heading} onReset={() => resetField("landing_security_intro", "heading")} defaultVal={DEFAULTS.landing_security_intro.heading} onChange={(v) => updateKey("landing_security_intro", "heading", v)} testId="cms-sec-heading" />
                        </FieldRow>
                        <div className="text-xs text-[#17212B]/60 italic">Security tile cards (bcrypt, tenant isolation, audit logs, dual approval, signed webhooks, verified registration) are code-defined for auditability. Contact engineering to alter their copy.</div>
                    </SectionCard>
                </TabsContent>

                {/* TESTIMONIALS */}
                <TabsContent value="testimonials" className="mt-4">
                    <ArrayCard title="Testimonials"
                        section="testimonials" items={content.testimonials || []}
                        fields={[{ key: "quote", label: "Quote", isTextarea: true }, { key: "author", label: "Author" }, { key: "role", label: "Role/Specialty" }]}
                        onUpdate={updateArrayItem}
                        onAdd={() => addArrayItem("testimonials", { quote: "", author: "", role: "" })}
                        onRemove={removeArrayItem}
                        onSave={() => save("testimonials")}
                        onReset={() => resetSection("testimonials")}
                        saving={saving} testId="cms-testimonials" />
                </TabsContent>

                {/* FAQ */}
                <TabsContent value="faqs" className="mt-4">
                    <ArrayCard title="FAQs" section="faqs" items={content.faqs || []}
                        fields={[{ key: "question", label: "Question" }, { key: "answer", label: "Answer", isTextarea: true }]}
                        onUpdate={updateArrayItem}
                        onAdd={() => addArrayItem("faqs", { question: "", answer: "" })}
                        onRemove={removeArrayItem}
                        onSave={() => save("faqs")}
                        onReset={() => resetSection("faqs")}
                        saving={saving} testId="cms-faqs" />
                </TabsContent>

                {/* FINAL CTA */}
                <TabsContent value="final_cta" className="mt-4">
                    <SectionCard title="Final CTA" onSave={() => save("landing_final_cta")} onReset={() => resetSection("landing_final_cta")} saving={saving} testId="cms-final">
                        <TextareaField label="Heading" rows={2} value={content.landing_final_cta?.heading} onReset={() => resetField("landing_final_cta", "heading")} defaultVal={DEFAULTS.landing_final_cta.heading} onChange={(v) => updateKey("landing_final_cta", "heading", v)} testId="cms-final-heading" />
                        <TextareaField label="Body" rows={3} value={content.landing_final_cta?.body} onReset={() => resetField("landing_final_cta", "body")} defaultVal={DEFAULTS.landing_final_cta.body} onChange={(v) => updateKey("landing_final_cta", "body", v)} testId="cms-final-body" />
                        <FieldRow>
                            <FieldCol label="Primary CTA" value={content.landing_final_cta?.cta_primary} onReset={() => resetField("landing_final_cta", "cta_primary")} defaultVal={DEFAULTS.landing_final_cta.cta_primary} onChange={(v) => updateKey("landing_final_cta", "cta_primary", v)} testId="cms-final-cta1" />
                            <FieldCol label="Secondary CTA" value={content.landing_final_cta?.cta_secondary} onReset={() => resetField("landing_final_cta", "cta_secondary")} defaultVal={DEFAULTS.landing_final_cta.cta_secondary} onChange={(v) => updateKey("landing_final_cta", "cta_secondary", v)} testId="cms-final-cta2" />
                        </FieldRow>
                        <FieldCol label="Disclaimer" value={content.landing_final_cta?.disclaimer} onReset={() => resetField("landing_final_cta", "disclaimer")} defaultVal={DEFAULTS.landing_final_cta.disclaimer} onChange={(v) => updateKey("landing_final_cta", "disclaimer", v)} testId="cms-final-disclaimer" />
                    </SectionCard>
                </TabsContent>

                {/* FOOTER */}
                <TabsContent value="footer" className="mt-4">
                    <SectionCard title="Footer" onSave={() => save("footer")} onReset={() => resetSection("footer")} saving={saving} testId="cms-footer">
                        <TextareaField label="Tagline" rows={2} value={content.footer?.tagline} onReset={() => resetField("footer", "tagline")} defaultVal={DEFAULTS.footer.tagline} onChange={(v) => updateKey("footer", "tagline", v)} testId="cms-footer-tagline" />
                        <TextareaField label="Legal notice" rows={2} value={content.footer?.legal} onReset={() => resetField("footer", "legal")} defaultVal={DEFAULTS.footer.legal} onChange={(v) => updateKey("footer", "legal", v)} testId="cms-footer-legal" />
                    </SectionCard>
                </TabsContent>

                {/* CONTACT */}
                <TabsContent value="contact" className="mt-4">
                    <SectionCard title="Contact" onSave={() => save("contact")} onReset={() => resetSection("contact")} saving={saving} testId="cms-contact">
                        <FieldRow>
                            <FieldCol label="Support email" value={content.contact?.support_email} onReset={() => resetField("contact", "support_email")} defaultVal={DEFAULTS.contact.support_email} onChange={(v) => updateKey("contact", "support_email", v)} testId="cms-contact-email" />
                            <FieldCol label="Support phone" value={content.contact?.support_phone} onReset={() => resetField("contact", "support_phone")} defaultVal={DEFAULTS.contact.support_phone} onChange={(v) => updateKey("contact", "support_phone", v)} testId="cms-contact-phone" />
                        </FieldRow>
                        <TextareaField label="Office address" rows={2} value={content.contact?.office_address} onReset={() => resetField("contact", "office_address")} defaultVal={DEFAULTS.contact.office_address} onChange={(v) => updateKey("contact", "office_address", v)} testId="cms-contact-address" />
                    </SectionCard>
                </TabsContent>

                {/* SEO */}
                <TabsContent value="seo" className="mt-4">
                    <SectionCard title="Marketing SEO meta" onSave={() => save("marketing_seo")} onReset={() => resetSection("marketing_seo")} saving={saving} testId="cms-seo">
                        <p className="text-xs text-[#17212B]/60">Per-route title + meta description for the marketing pages. Public /dr/{'<slug>'} pages have their own AI-refined SEO and are unaffected.</p>
                        {["home", "pricing", "about", "contact"].map((route) => (
                            <div key={route} className="border rounded-md p-3 space-y-2" data-testid={`cms-seo-${route}`}>
                                <div className="text-xs font-bold uppercase tracking-widest text-[#123B5D]">/{route === "home" ? "" : route}</div>
                                <Input placeholder="<title>" value={content.marketing_seo?.[route]?.title || ""}
                                    onChange={(e) => setContent({ ...content, marketing_seo: { ...content.marketing_seo, [route]: { ...(content.marketing_seo?.[route] || {}), title: e.target.value } } })}
                                    data-testid={`cms-seo-${route}-title`} />
                                <Textarea rows={2} placeholder="<meta name='description'>"
                                    value={content.marketing_seo?.[route]?.description || ""}
                                    onChange={(e) => setContent({ ...content, marketing_seo: { ...content.marketing_seo, [route]: { ...(content.marketing_seo?.[route] || {}), description: e.target.value } } })}
                                    data-testid={`cms-seo-${route}-desc`} />
                            </div>
                        ))}
                    </SectionCard>
                </TabsContent>

                {/* LEGAL */}
                <TabsContent value="legal" className="mt-4 space-y-4">
                    <SectionCard title="Terms" onSave={() => save("terms_html")} onReset={() => setContent({ ...content, terms_html: "" })} saving={saving} testId="cms-terms">
                        <TextareaField rows={10} value={content.terms_html || ""} onChange={(v) => setContent({ ...content, terms_html: v })} testId="cms-terms-html" />
                    </SectionCard>
                    <SectionCard title="Privacy" onSave={() => save("privacy_html")} onReset={() => setContent({ ...content, privacy_html: "" })} saving={saving} testId="cms-privacy">
                        <TextareaField rows={10} value={content.privacy_html || ""} onChange={(v) => setContent({ ...content, privacy_html: v })} testId="cms-privacy-html" />
                    </SectionCard>
                    <SectionCard title="Refund" onSave={() => save("refund_html")} onReset={() => setContent({ ...content, refund_html: "" })} saving={saving} testId="cms-refund">
                        <TextareaField rows={8} value={content.refund_html || ""} onChange={(v) => setContent({ ...content, refund_html: v })} testId="cms-refund-html" />
                    </SectionCard>
                </TabsContent>

                {/* LAYOUT */}
                <TabsContent value="layout" className="mt-4">
                    <SectionCard title="Section visibility + order" onSave={() => save("landing_sections")} onReset={() => resetSection("landing_sections")} saving={saving} testId="cms-layout">
                        <p className="text-xs text-[#17212B]/60 mb-2">Toggle sections on/off and drag order. Hero + Final CTA are also toggleable but usually stay ON.</p>
                        <div className="space-y-1" data-testid="cms-layout-list">
                            {(content.landing_sections || DEFAULTS.landing_sections).map((s, i, arr) => (
                                <div key={s.id} className="flex items-center gap-2 border rounded px-3 py-2 bg-white"
                                    data-testid={`cms-layout-row-${s.id}`}>
                                    <span className="text-[10px] font-mono text-[#17212B]/50 w-6">{i + 1}.</span>
                                    <span className="flex-1 text-sm">{s.label || s.id}</span>
                                    <button onClick={() => moveSection(s.id, -1)} disabled={i === 0} className="p-1 disabled:opacity-30" data-testid={`cms-layout-up-${s.id}`}><ArrowUp size={14} /></button>
                                    <button onClick={() => moveSection(s.id, 1)} disabled={i === arr.length - 1} className="p-1 disabled:opacity-30" data-testid={`cms-layout-down-${s.id}`}><ArrowDown size={14} /></button>
                                    <button onClick={() => toggleSectionVisible(s.id)} className={"px-2 py-1 text-[11px] rounded-full font-semibold " + (s.visible ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500")}
                                        data-testid={`cms-layout-toggle-${s.id}`}>
                                        {s.visible ? <span className="flex items-center gap-1"><Eye size={11} />ON</span> : <span className="flex items-center gap-1"><EyeOff size={11} />OFF</span>}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </TabsContent>
            </Tabs>
        </AdminShell>
    );
}

// ── Reusable subs ────────────────────────────────────────────────────────
function SectionCard({ title, children, onSave, onReset, saving, testId }) {
    return (
        <Card className="border-[#123B5D]/10" data-testid={testId}>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle className="text-base">{title}</CardTitle>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={onReset} data-testid={`${testId}-reset`}>
                        <RotateCcw size={13} /> Reset
                    </Button>
                    <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
                        <Save size={13} /> {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">{children}</CardContent>
        </Card>
    );
}

function ArrayCard({ title, section, items, fields, onUpdate, onAdd, onRemove, onSave, onReset, saving, testId }) {
    return (
        <Card className="border-[#123B5D]/10" data-testid={testId}>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle className="text-base">{title} <span className="text-[#17212B]/50 text-xs">({items.length})</span></CardTitle>
                <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={onReset} data-testid={`${testId}-reset`}>
                        <RotateCcw size={13} /> Reset
                    </Button>
                    <Button variant="outline" size="sm" onClick={onAdd} data-testid={`${testId}-add`}>
                        <Plus size={13} /> Add
                    </Button>
                    <Button size="sm" onClick={onSave} disabled={saving} data-testid={`${testId}-save`}>
                        <Save size={13} /> {saving ? "Saving…" : "Save all"}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {items.length === 0 && (
                    <div className="text-sm text-[#17212B]/50 italic">No entries yet — click Add.</div>
                )}
                {items.map((item, idx) => (
                    <div key={idx} className="border border-[#123B5D]/10 rounded-md p-3 space-y-2" data-testid={`${testId}-row-${idx}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold text-[#17212B]/60">#{idx + 1}</span>
                            <button onClick={() => onRemove(section, idx)} className="text-[#DC2626] hover:text-[#B91C1C]" data-testid={`${testId}-remove-${idx}`}>
                                <Trash2 size={13} />
                            </button>
                        </div>
                        {fields.map((f) =>
                            f.isTextarea ? (
                                <TextareaField key={f.key} label={f.label} value={item[f.key] || ""}
                                    onChange={(v) => onUpdate(section, idx, f.key, v)}
                                    testId={`${testId}-${idx}-${f.key}`} rows={3} />
                            ) : (
                                <FieldCol key={f.key} label={f.label} value={item[f.key] || ""}
                                    onChange={(v) => onUpdate(section, idx, f.key, v)}
                                    testId={`${testId}-${idx}-${f.key}`} />
                            ),
                        )}
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}

function FieldRow({ children }) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>;
}

function FieldCol({ label, value, onChange, testId, defaultVal, onReset }) {
    const changed = defaultVal !== undefined && value !== undefined && value !== defaultVal;
    return (
        <div>
            <div className="flex items-center justify-between">
                <Label className="text-xs">{label}</Label>
                {onReset && changed && (
                    <button onClick={onReset} className="text-[10px] text-[#2563EB] hover:underline" data-testid={testId ? `${testId}-reset` : undefined}>
                        Reset field
                    </button>
                )}
            </div>
            <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </div>
    );
}

function TextareaField({ label, value, onChange, testId, rows = 2, defaultVal, onReset }) {
    const changed = defaultVal !== undefined && value !== undefined && value !== defaultVal;
    return (
        <div>
            <div className="flex items-center justify-between">
                {label && <Label className="text-xs">{label}</Label>}
                {onReset && changed && (
                    <button onClick={onReset} className="text-[10px] text-[#2563EB] hover:underline" data-testid={testId ? `${testId}-reset` : undefined}>
                        Reset field
                    </button>
                )}
            </div>
            <Textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </div>
    );
}
